import { useContext, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  selectedDateAtom,
  selectedMonthAtom,
  selectedYearAtom,
} from 'atoms/dateSelector';
import { setYear, setMonth } from 'date-fns';
import withAuth from 'hocs/withAuth/withAuth';
import { useAtom, useAtomValue } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { getMaxAndMinDateLimit } from 'utils/helpers';

import { NotificationContext, useModals } from 'context';
import {
  getSessionHistory,
  getSessionHistorySummary,
  SpecialistRole,
} from 'services/specialist';

import {
  SessionsPaymentsTabList,
  PageHeader,
  DateSelector,
  InvoiceStatus,
} from 'components';
import { HistoryResume } from 'components/HistoryResume/HistoryResume';
import Paginator from 'components/Paginator';
import SessionsHistoryTable from 'components/SessionsHistoryTable';
import { Ordering } from 'components/SessionsHistoryTable/types';
import { FeedbackComponentProps } from 'modals/Feedback';

import { maxDateSelection, minDateSelection } from './config';

import { SessionHistoryData, SessionRecord, Summary } from './types';

import * as S from './styles';

const initialCursor = { 1: '' };

const SessionHistory: React.FC = () => {
  const intl = useIntl();

  const [cursors, setCursors] =
    useState<{ [key: string]: string }>(initialCursor);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [currentOrdering, setCurrentOrdering] = useState<Ordering>(
    {} as Ordering,
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedYear = useAtomValue(selectedYearAtom);
  const selectedMonth = useAtomValue(selectedMonthAtom);
  const [selectedDate, setSelectedDate] = useAtom(selectedDateAtom);

  const [, setClosedInvoice] = useState(true);

  const { openModal } = useModals();

  const { setNotification } = useContext(NotificationContext);

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getUTCFullYear();
  const paramYear = searchParams.get('year');
  const paramMonth = searchParams.get('month');

  useEffect(() => {
    setSearchParams(urlParams => {
      if (!paramYear) {
        urlParams.set('year', String(currentYear));
      }

      if (!paramMonth) {
        urlParams.set('month', String(currentMonth));
      }

      return urlParams;
    });

    if (paramYear && paramMonth) {
      const searchedDate = setYear(
        setMonth(new Date(), Number(paramMonth) - 1),
        Number(paramYear),
      );

      setSelectedDate(searchedDate);
      return;
    }

    setSelectedDate(currentDate);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { currenDateAfterLimit, currenDateBeforeLimit } =
      getMaxAndMinDateLimit(selectedDate, maxDateSelection, minDateSelection);

    if (currenDateAfterLimit || currenDateBeforeLimit) {
      setSelectedDate(currentDate);

      setSearchParams(urlParams => {
        urlParams.set('year', String(currentYear));
        urlParams.set('month', String(currentMonth));

        return urlParams;
      });
    }
  }, [
    currentDate,
    currentMonth,
    currentYear,
    selectedDate,
    setSearchParams,
    setSelectedDate,
  ]);

  const handleDateSelection = () => {
    setPageNumber(1);
    setCursors(initialCursor);

    setSearchParams(urlParams => {
      urlParams.set('year', String(selectedYear));
      urlParams.set('month', String(selectedMonth));

      return urlParams;
    });
  };

  const handleOrdering = (columnkey: string) => {
    let direction;

    if (!currentOrdering?.direction || currentOrdering.column !== columnkey) {
      direction = 'desc';
    } else if (currentOrdering.direction === 'desc') {
      direction = 'asc';
    } else {
      setCurrentOrdering({} as Ordering);
      return;
    }

    setCurrentOrdering({ column: columnkey, direction });
  };

  const getSessionHistoryData = async (
    year: number,
    month: number,
    page: number,
    ordering?: Ordering,
  ): Promise<SessionHistoryData> => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { results, metadata } = await getSessionHistory(
        year,
        month,
        timezone,
        cursors[page],
        ordering?.column,
        ordering?.direction,
      );

      const sessions: SessionRecord[] = results.map(result => ({
        startAt: result.start_at,
        status: result.status,
        actionType: result.action_type,
        initialSession: result.initial_session,
        userFullName: result.end_user_full_name,
        inviteStatus: result.invite_status,
      }));

      if (metadata.nextCursor !== '') {
        cursors[page + 1] = metadata.nextCursor;
      }

      const history: SessionHistoryData = {
        results: sessions,
        metadata,
      };

      return history;
    } catch (e) {
      const fallbackMessage = 'Error retrieving session history';

      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: fallbackMessage,
          message: 'ERROR.TRY',
          status: 'error',
        },
      });

      return {} as SessionHistoryData;
    }
  };

  const getSessionHistorySummaryData = async (year: number, month: number) => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const data = await getSessionHistorySummary(year, month, timezone);

      const sessionsSummary: Summary = {
        completed: {
          initial: data.completed?.initial_session_amount,
          regular: data.completed?.regular_session_amount,
          accepted: data.completed?.accepted_session_amount,
        },
        totalSessions: data.total_sessions,
        lateCancel: data.late_cancel,
        noShow: data.no_show,
      };

      return sessionsSummary;
    } catch (e) {
      const fallbackMessage = 'Error retrieving session history summary';

      openModal<FeedbackComponentProps>('feedback', {
        props: {
          title: fallbackMessage,
          message: 'ERROR.TRY',
          status: 'error',
        },
      });

      return {
        completed: 0,
        lateCancel: 0,
        noShow: 0,
        totalSessions: 0,
        welcome: 0,
      } as Summary;
    }
  };

  const { data: history, isLoading: sessionsLoading } = useQuery({
    queryKey: [paramYear, paramMonth, pageNumber, currentOrdering, 'history'],
    cacheTime: 0,
    enabled: !!(paramYear && paramMonth),
    queryFn: () =>
      getSessionHistoryData(
        Number(paramYear),
        Number(paramMonth),
        pageNumber,
        currentOrdering,
      ),
    onError: () => {
      setNotification({
        message: intl.formatMessage({
          id: 'CPPLATFORM.FEEDBACKGENERICERROR.TRYAGAINLATER',
        }),
        type: 'failure',
      });
    },
  });

  const { data: historySummary, isLoading: summaryLoading } = useQuery({
    queryKey: [paramYear, paramMonth, 'summary'],
    enabled: !!(paramYear && paramMonth),
    queryFn: () =>
      getSessionHistorySummaryData(Number(paramYear), Number(paramMonth)),
    onError: () => {
      setNotification({
        message: intl.formatMessage({
          id: 'CPPLATFORM.FEEDBACKGENERICERROR.TRYAGAINLATER',
        }),
        type: 'failure',
      });
    },
  });

  const nextSessionListPage = () => {
    const nextPage = pageNumber + 1;
    if (nextPage > (history?.metadata?.totalPages || 0)) return;
    setPageNumber(nextPage);
  };

  const previousSessionListPage = () => {
    if (pageNumber === 1) return;
    const previousPage = pageNumber - 1;
    setPageNumber(previousPage);
  };

  return (
    <S.Container>
      <PageHeader>
        <S.HeaderLabel data-testid="pageTitle">
          <FormattedMessage id="CPPAYMENTS.TABTITLE.SESSIONPAYMENTS" />
        </S.HeaderLabel>
      </PageHeader>
      <S.SubHeaderContainer data-testid="payments-sub-header-container">
        <SessionsPaymentsTabList
          selectedIndex={1}
          year={paramYear as string}
          month={paramMonth as string}
        />
        <S.DatePaginationContainer>
          <InvoiceStatus
            year={Number(paramYear)}
            month={Number(paramMonth)}
            onChangeStatus={status => setClosedInvoice(status === 'closed')}
          />
          {paramYear && paramMonth && (
            <DateSelector
              minDate={minDateSelection}
              maxDate={maxDateSelection}
              onDateSelection={handleDateSelection}
            />
          )}
        </S.DatePaginationContainer>
      </S.SubHeaderContainer>
      <S.DataContainer>
        <S.DataCard>
          <S.BigNumbersContainer>
            <HistoryResume
              data={historySummary || {}}
              loading={summaryLoading}
            />
            <S.AdjustLink
              small
              target="blank"
              rel="noopener noreferrer nofollow"
              href="https://wellz.zendesk.com/hc/pt-br/requests/new"
            >
              <FormattedMessage id="SESSION.HISTORY.CP.BUTTON.ADJUSTMENT" />
            </S.AdjustLink>
            <S.AdjustWarning>
              <FormattedMessage id="SESSION.HISTORY.CP.TEXT.WARNING" />
            </S.AdjustWarning>
          </S.BigNumbersContainer>
          <S.SessionsTablecontainer>
            <SessionsHistoryTable
              data={history?.results || []}
              loading={sessionsLoading}
              currentOrdering={currentOrdering}
              orderData={handleOrdering}
            />
            <S.PaginatorContainer>
              <Paginator
                startPage={pageNumber}
                lastPage={history?.metadata.totalPages}
                next={nextSessionListPage}
                previous={previousSessionListPage}
              />
            </S.PaginatorContainer>
          </S.SessionsTablecontainer>
        </S.DataCard>
      </S.DataContainer>
    </S.Container>
  );
};

export default withAuth(SessionHistory, [SpecialistRole.CP]);
